import type { SupportKind, TerminationKind } from "@/lib/pipe-spec";

export interface Pt {
  x: number;
  y: number;
}

/**
 * Single-line ISO symbols for pipe supports.
 * `at` is the screen point on the pipe centreline; `along` is the screen-space
 * unit direction of the pipe so the symbol can straddle it correctly.
 */
export function SupportSymbol({
  kind,
  at,
  along,
  vertical = false,
  scale = 1,
  ghost = false,
  mark,
}: {
  kind: SupportKind;
  at: Pt;
  along: Pt;
  /** the pipe here runs up/down — hangers become riser clamps visually */
  vertical?: boolean;
  scale?: number;
  ghost?: boolean;
  mark?: string;
}) {
  const l = Math.hypot(along.x, along.y) || 1;
  const ux = along.x / l;
  const uy = along.y / l;
  const px = -uy;
  const py = ux;
  const h = 7 * scale;

  const stroke = ghost ? "hsl(var(--primary))" : "hsl(var(--foreground))";
  const fill = "hsl(var(--background))";
  const common = {
    stroke,
    strokeWidth: 1.5 * scale,
    fill: "none" as const,
    opacity: ghost ? 0.8 : 1,
  };
  /** point offset perpendicular to the pipe (negative = "up" on the sheet) */
  const off = (s: number, t = 0) => ({
    x: at.x + px * s + ux * t,
    y: at.y + py * s + uy * t,
  });
  const up = vertical ? { x: 0, y: -1 } : { x: 0, y: -1 };
  const upPt = (s: number, t = 0) => ({
    x: at.x + up.x * s + ux * t,
    y: at.y + up.y * s + uy * t,
  });
  const line = (a: Pt, b: Pt, w = 1.5) => (
    <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} {...common} strokeWidth={w * scale} />
  );

  /** steel above: a short bar the hanger hangs from */
  const steelBar = (
    <line
      x1={upPt(h * 2.6, -h * 1.2).x}
      y1={upPt(h * 2.6, -h * 1.2).y}
      x2={upPt(h * 2.6, h * 1.2).x}
      y2={upPt(h * 2.6, h * 1.2).y}
      {...common}
      strokeWidth={2 * scale}
    />
  );

  let body: JSX.Element;
  switch (kind) {
    case "trapeze":
      body = (
        <>
          {steelBar}
          {line(upPt(h * 2.6, -h * 1.2), upPt(h * 0.6, -h * 1.2), 1.2)}
          {line(upPt(h * 2.6, h * 1.2), upPt(h * 0.6, h * 1.2), 1.2)}
          {line(upPt(h * 0.6, -h * 1.6), upPt(h * 0.6, h * 1.6), 2)}
        </>
      );
      break;
    case "guide":
      body = (
        <>
          <rect
            x={at.x - h}
            y={at.y - h}
            width={h * 2}
            height={h * 2}
            {...common}
            fill={fill}
            transform={`rotate(${(Math.atan2(uy, ux) * 180) / Math.PI} ${at.x} ${at.y})`}
          />
          {line(off(-h * 1.5), off(h * 1.5), 1.2)}
        </>
      );
      break;
    case "anchor":
      body = (
        <>
          <path
            d={`M ${off(-h * 1.4).x} ${off(-h * 1.4).y} L ${off(h * 1.4).x} ${off(h * 1.4).y}`}
            {...common}
            strokeWidth={2.4 * scale}
          />
          <path
            d={`M ${off(-h, -h).x} ${off(-h, -h).y} L ${at.x} ${at.y} L ${off(-h, h).x} ${
              off(-h, h).y
            } Z`}
            {...common}
            fill={stroke}
            stroke="none"
          />
        </>
      );
      break;
    case "spring":
      body = (
        <>
          {steelBar}
          <path
            d={`M ${upPt(h * 2.4).x} ${upPt(h * 2.4).y} l ${h * 0.8} ${-0} l ${-h * 1.6} ${
              h * 0.5
            } l ${h * 1.6} ${h * 0.5} l ${-h * 1.6} ${h * 0.5} l ${h * 0.8} ${h * 0.4}`}
            {...common}
            strokeWidth={1.2 * scale}
          />
          {line(upPt(h * 0.5), at, 1.2)}
        </>
      );
      break;
    case "shoe":
      body = (
        <>
          <rect
            x={at.x - h}
            y={at.y}
            width={h * 2}
            height={h * 0.9}
            {...common}
            fill={fill}
            strokeWidth={1.3 * scale}
          />
          {line({ x: at.x - h * 1.5, y: at.y + h * 0.9 }, { x: at.x + h * 1.5, y: at.y + h * 0.9 }, 2)}
        </>
      );
      break;
    case "roller":
      body = (
        <>
          <circle cx={at.x} cy={at.y + h * 0.7} r={h * 0.6} {...common} fill={fill} />
          {line({ x: at.x - h * 1.5, y: at.y + h * 1.4 }, { x: at.x + h * 1.5, y: at.y + h * 1.4 }, 2)}
        </>
      );
      break;
    case "riserClamp":
      body = (
        <>
          {line({ x: at.x - h * 1.6, y: at.y }, { x: at.x + h * 1.6, y: at.y }, 2.2)}
          {line({ x: at.x - h * 1.6, y: at.y - h * 0.6 }, { x: at.x - h * 1.6, y: at.y + h * 0.6 }, 1.2)}
          {line({ x: at.x + h * 1.6, y: at.y - h * 0.6 }, { x: at.x + h * 1.6, y: at.y + h * 0.6 }, 1.2)}
        </>
      );
      break;
    case "hanger":
    default:
      body = (
        <>
          {steelBar}
          {line(upPt(h * 2.6), upPt(h * 0.8), 1.2)}
          {/* clevis */}
          <path
            d={`M ${upPt(h * 0.9, -h * 0.7).x} ${upPt(h * 0.9, -h * 0.7).y} L ${
              upPt(-h * 0.2, -h * 0.7).x
            } ${upPt(-h * 0.2, -h * 0.7).y} M ${upPt(h * 0.9, h * 0.7).x} ${
              upPt(h * 0.9, h * 0.7).y
            } L ${upPt(-h * 0.2, h * 0.7).x} ${upPt(-h * 0.2, h * 0.7).y}`}
            {...common}
            strokeWidth={1.2 * scale}
          />
        </>
      );
      break;
  }

  return (
    <g>
      {body}
      {mark && (
        <text
          x={at.x + h * 2}
          y={at.y - h * 1.6}
          className="fill-muted-foreground"
          style={{ fontSize: 9 * scale, fontFamily: "ui-monospace, monospace" }}
        >
          {mark}
        </text>
      )}
    </g>
  );
}

/**
 * End-of-line symbols: caps, flanged ends and equipment nozzles.
 * `dir` points OUT of the pipe in screen space.
 */
export function TerminationSymbol({
  kind,
  at,
  dir,
  scale = 1,
  ghost = false,
  mark,
}: {
  kind: TerminationKind;
  at: Pt;
  dir: Pt;
  scale?: number;
  ghost?: boolean;
  mark?: string;
}) {
  const l = Math.hypot(dir.x, dir.y) || 1;
  const ux = dir.x / l;
  const uy = dir.y / l;
  const px = -uy;
  const py = ux;
  const h = 7 * scale;

  const stroke = ghost ? "hsl(var(--primary))" : "hsl(var(--foreground))";
  const fill = "hsl(var(--background))";
  const common = {
    stroke,
    strokeWidth: 1.6 * scale,
    fill: "none" as const,
    opacity: ghost ? 0.8 : 1,
  };
  const at2 = (t: number, s = 0) => ({ x: at.x + ux * t + px * s, y: at.y + uy * t + py * s });
  const tick = (t: number, size = 1.2, w = 1.6) => (
    <line
      x1={at2(t, -h * size).x}
      y1={at2(t, -h * size).y}
      x2={at2(t, h * size).x}
      y2={at2(t, h * size).y}
      {...common}
      strokeWidth={w * scale}
    />
  );
  const boxAt = (t: number, letter: string) => (
    <>
      <rect
        x={at2(t).x - h * 1.4}
        y={at2(t).y - h * 1.1}
        width={h * 2.8}
        height={h * 2.2}
        rx={2}
        {...common}
        fill={fill}
      />
      <text
        x={at2(t).x}
        y={at2(t).y + h * 0.45}
        textAnchor="middle"
        className="fill-foreground"
        style={{ fontSize: 9 * scale, fontFamily: "ui-monospace, monospace" }}
      >
        {letter}
      </text>
    </>
  );

  let body: JSX.Element;
  switch (kind) {
    case "flangedEnd":
      body = (
        <>
          {tick(0)}
          {tick(h * 0.7)}
        </>
      );
      break;
    case "blindEnd":
      body = (
        <>
          {tick(0)}
          <path
            d={`M ${at2(h * 0.4, -h * 1.2).x} ${at2(h * 0.4, -h * 1.2).y} L ${
              at2(h * 1.0, -h * 1.2).x
            } ${at2(h * 1.0, -h * 1.2).y} L ${at2(h * 1.0, h * 1.2).x} ${
              at2(h * 1.0, h * 1.2).y
            } L ${at2(h * 0.4, h * 1.2).x} ${at2(h * 0.4, h * 1.2).y} Z`}
            fill={stroke}
            stroke="none"
            opacity={common.opacity}
          />
        </>
      );
      break;
    case "pumpNozzle":
      body = (
        <>
          {tick(0)}
          <circle cx={at2(h * 1.8).x} cy={at2(h * 1.8).y} r={h * 1.3} {...common} fill={fill} />
          <text
            x={at2(h * 1.8).x}
            y={at2(h * 1.8).y + h * 0.45}
            textAnchor="middle"
            className="fill-foreground"
            style={{ fontSize: 9 * scale, fontFamily: "ui-monospace, monospace" }}
          >
            P
          </text>
        </>
      );
      break;
    case "vesselNozzle":
      body = (
        <>
          {tick(0)}
          {boxAt(h * 1.9, "V")}
        </>
      );
      break;
    case "exchangerNozzle":
      body = (
        <>
          {tick(0)}
          {boxAt(h * 1.9, "HX")}
        </>
      );
      break;
    case "tankNozzle":
      body = (
        <>
          {tick(0)}
          {boxAt(h * 1.9, "TK")}
        </>
      );
      break;
    case "equipment":
      body = (
        <>
          {tick(0)}
          {boxAt(h * 1.9, "EQ")}
        </>
      );
      break;
    case "openEnd":
      body = (
        <path
          d={`M ${at2(0, -h).x} ${at2(0, -h).y} L ${at2(h * 1.1).x} ${at2(h * 1.1).y} L ${
            at2(0, h).x
          } ${at2(0, h).y}`}
          {...common}
        />
      );
      break;
    case "futureConn":
      body = (
        <>
          {tick(0)}
          <path
            d={`M ${at2(h * 0.6, -h).x} ${at2(h * 0.6, -h).y} L ${at2(h * 1.8, -h).x} ${
              at2(h * 1.8, -h).y
            } L ${at2(h * 1.8, h).x} ${at2(h * 1.8, h).y} L ${at2(h * 0.6, h).x} ${
              at2(h * 0.6, h).y
            }`}
            {...common}
            strokeDasharray="3 3"
          />
        </>
      );
      break;
    case "cap":
    default:
      body = (
        <>
          <path
            d={`M ${at2(0, -h).x} ${at2(0, -h).y} L ${at2(h * 0.8, -h).x} ${
              at2(h * 0.8, -h).y
            } A ${h} ${h} 0 0 1 ${at2(h * 0.8, h).x} ${at2(h * 0.8, h).y} L ${at2(0, h).x} ${
              at2(0, h).y
            }`}
            {...common}
            fill={fill}
          />
        </>
      );
      break;
  }

  return (
    <g>
      {body}
      {mark && (
        <text
          x={at2(h * 2.2, -h * 2).x}
          y={at2(h * 2.2, -h * 2).y}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: 9 * scale, fontFamily: "ui-monospace, monospace" }}
        >
          {mark}
        </text>
      )}
    </g>
  );
}
